import type { GenreConfig } from './types';

export const JAZZ: GenreConfig = {
  id: 'jazz',
  siteName: 'JazzAlbumReviews',
  domain: 'jazzalbumreviews.com',
  noun: 'jazz',
  label: 'Jazz',
  shortName: 'JAR',
  tagline: 'In-Depth Jazz Album Reviews',
  description:
    'In-depth jazz album reviews with a published scorecard: composition, recording, performance and cohesion, from bebop and hard bop classics to modern jazz.',
  readerLine: 'a serious jazz listener',
  xHandle: null,
  gaId: 'G-B2NHE2CCVW',
  ahrefsKey: '4kWhcQ1F+607mIfQZPlp7A',
  clarityId: 'z38pblxle5',
  accentHue: 210,

  dailyReviews: 1,
  launchReviews: 20,
  trackLimits: { min: 3, max: 30 },

  musicbrainzTags: ['jazz', 'hard bop', 'bebop', 'modal jazz', 'free jazz', 'jazz fusion', 'cool jazz', 'post-bop', 'spiritual jazz'],
  wikipediaArtistSuffixes: ['(musician)', '(saxophonist)', '(pianist)', '(trumpeter)', '(band)'],
  wikipediaArtistSearchTerm: 'jazz musician',
  artistKeywordSuffix: 'jazz',

  scorecard: {
    dimensions: [
      {
        key: 'writing',
        label: 'Composition',
        weight: 0.2,
        measures:
          'Themes, arrangements, harmonic ideas and the frameworks built for improvisation: whether the written material gives the players something worth playing on.',
      },
      {
        key: 'production',
        label: 'Recording',
        weight: 0.15,
        measures:
          'Sound, balance, room and engineering: how well the record captures the band, from the presence of the rhythm section to the placement of the horns.',
      },
      {
        key: 'delivery',
        label: 'Performance',
        weight: 0.3,
        measures:
          'Improvisation, interplay, ensemble feel and solos: the quality of the playing itself and how the musicians respond to one another.',
      },
      {
        key: 'cohesion',
        label: 'Cohesion',
        weight: 0.15,
        measures:
          'Sequencing, pacing, length and filler: whether it works as an album rather than a collection of takes.',
      },
      {
        key: 'originality',
        label: 'Originality',
        weight: 0.1,
        measures:
          "What it adds, measured against the leader's own catalog and the peers of its moment, not against what came later.",
      },
      {
        key: 'replay',
        label: 'Replay value',
        weight: 0.1,
        measures:
          'How it holds up on repeat listens and over time. For recent releases this is a projection and the note says so.',
      },
    ],
    topBandMeaning: 'Among the best jazz albums ever made on this measure.',
  },

  canonicalGenres: [
    'Bebop',
    'Hard Bop',
    'Cool Jazz',
    'Modal Jazz',
    'Free Jazz',
    'Jazz Fusion',
    'Post-Bop',
    'Spiritual Jazz',
    'Big Band',
    'Vocal Jazz',
    'Latin Jazz',
    'Contemporary Jazz',
  ],

  primaryGenres: [
    { label: 'Bebop', slug: 'bebop', hue: 20, styles: ['bebop', 'swing', 'early-modern-jazz', 'dixieland'] },
    { label: 'Hard Bop', slug: 'hard-bop', hue: 210, styles: ['soul-jazz', 'funky-jazz', 'organ-jazz', 'east-coast-jazz'] },
    { label: 'Cool Jazz', slug: 'cool-jazz', hue: 190, styles: ['west-coast-jazz', 'third-stream', 'chamber-jazz'] },
    { label: 'Modal Jazz', slug: 'modal-jazz', hue: 240, styles: ['modal-jazz', 'jazz-piano-trio', 'mainstream-jazz'] },
    { label: 'Free Jazz', slug: 'free-jazz', hue: 0, styles: ['avant-garde-jazz', 'free-improvisation', 'loft-jazz', 'aacm'] },
    { label: 'Jazz Fusion', slug: 'jazz-fusion', hue: 300, styles: ['jazz-rock', 'jazz-funk', 'electric-jazz', 'smooth-jazz'] },
    { label: 'Post-Bop', slug: 'post-bop', hue: 160, styles: ['post-bop', 'm-base', 'neo-bop', 'modern-creative'] },
    { label: 'Spiritual Jazz', slug: 'spiritual-jazz', hue: 45, styles: ['spiritual-jazz', 'afro-jazz', 'cosmic-jazz', 'modal-spiritual'] },
    { label: 'Big Band', slug: 'big-band', hue: 30, styles: ['big-band', 'orchestral-jazz', 'large-ensemble', 'swing-era'] },
    { label: 'Vocal Jazz', slug: 'vocal-jazz', hue: 340, styles: ['jazz-vocals', 'vocalese', 'jazz-standards', 'torch-song'] },
    { label: 'Latin Jazz', slug: 'latin-jazz', hue: 90, styles: ['afro-cuban-jazz', 'bossa-nova', 'brazilian-jazz', 'latin-jazz'] },
    { label: 'Contemporary Jazz', slug: 'contemporary-jazz', hue: 270, styles: ['nu-jazz', 'ecm-style', 'jazz-fusion-revival', 'electronic-jazz'] },
  ],

  genreDescriptions: {
    bebop:
      'Fast, harmonically dense small-group jazz built for virtuosity. Bebop took shape in the early 1940s around Charlie Parker, Dizzy Gillespie, Thelonious Monk and Bud Powell, shifting jazz from dance music toward a listener-focused art form.',
    'hard-bop':
      'A harder-edged extension of bebop that pulled in blues, gospel and rhythm and blues. Hard bop flourished in the 1950s and early 1960s through Art Blakey and the Jazz Messengers, Horace Silver and Blue Note, Prestige and Riverside sessions.',
    'cool-jazz':
      'Relaxed tempos, softer tone and more arranged textures. Cool jazz emerged around the 1949-50 Miles Davis sessions later released as Birth of the Cool and was carried on by Gerry Mulligan, Dave Brubeck and Chet Baker, with a strong presence on the American West Coast.',
    'modal-jazz':
      'Improvisation built on scales rather than fast chord changes. Modal jazz was developed in the late 1950s by Miles Davis and George Russell, and Kind of Blue (1959) remains its best-known statement, with John Coltrane and Bill Evans among the players who followed it.',
    'free-jazz':
      'Jazz that loosens or drops fixed chord changes, tempo and form. Free jazz became a movement around Ornette Coleman, Cecil Taylor and later John Coltrane and Albert Ayler, and extended through Chicago groups such as the AACM.',
    'jazz-fusion':
      'Jazz improvisation combined with rock rhythms and electric instruments. Fusion developed in the late 1960s and 1970s through Miles Davis, Weather Report, Mahavishnu Orchestra, Herbie Hancock and Return to Forever.',
    'post-bop':
      'Small-group jazz that absorbs bebop, modal and free ideas without belonging to a single one of them. Post-bop took shape in the 1960s, including the Miles Davis quintet of that decade and many Blue Note records, and remains a main thread of modern mainstream jazz.',
    'spiritual-jazz':
      'Jazz shaped by devotional and cosmic themes, long forms and collective energy. Spiritual jazz grew most visibly out of the later work of John Coltrane, such as A Love Supreme, and was extended by Pharoah Sanders, Alice Coltrane and others.',
    'big-band':
      'Large ensembles built on sectioned brass and reeds, written arrangements and featured soloists. Big bands defined the swing era of the 1930s and 1940s with Duke Ellington, Count Basie and Benny Goodman, and the form continues through modern repertory and composer-led orchestras.',
    'vocal-jazz':
      'Jazz where the singer is the lead instrument. Vocal jazz runs from Louis Armstrong, Billie Holiday and Ella Fitzgerald through Sarah Vaughan, Nina Simone and beyond, often built on the Great American Songbook.',
    'latin-jazz':
      'Jazz blended with Afro-Cuban and Brazilian rhythms. Latin jazz took shape in the 1940s through Dizzy Gillespie and Machito and Chano Pozo, and bossa nova brought a Brazilian strand to American jazz in the 1960s through Stan Getz and Joao Gilberto.',
    'contemporary-jazz':
      'Jazz since roughly 1990 that draws on the full history of the music alongside hip-hop, electronic and global influences. The scene includes ECM-style chamber records, hard-swinging acoustic groups and younger players working across genres.',
  },

  genreGuides: {
    bebop: {
      sound:
        'Bebop is built on fast tempos, complex chord changes, angular melodic lines and small combos built around saxophone, trumpet, piano, bass and drums. Heads are often written over standard chord progressions and played in unison before solos begin.',
      listenFor:
        'Our scores weight performance most heavily, at 30%, because bebop lives in improvisation and interplay. Recording matters, but older sessions are judged against what the technology of the time could capture, not against modern fidelity.',
    },
    'hard-bop': {
      sound:
        'Hard bop keeps bebop speed and harmony but adds a heavier groove, bluesy phrasing and gospel-influenced melodies. The rhythm section pushes harder and horn lines are often written in strong, memorable themes.',
      listenFor:
        'The scorecard looks at whether the themes give the solos a reason to exist, whether the rhythm section drives rather than accompanies, and whether the recording lets the band sound like it is in one room.',
    },
    'modal-jazz': {
      sound:
        'Modal jazz uses long stretches on a single scale or a few chords, giving soloists space to develop melodic ideas instead of navigating rapid changes. Textures are often sparse and the pacing slower.',
      listenFor:
        'Composition and performance work together here: the scorecard asks whether the framework is distinctive and whether the soloists use the space to say something, rather than treating the open harmony as a reason to wander.',
    },
    'free-jazz': {
      sound:
        'Free jazz loosens or drops fixed chord changes, tempo and form, favoring collective improvisation, extended technique and raw timbre. Pieces can be short and sharp or run for a whole side.',
      listenFor:
        'The scorecard judges structure within freedom: whether the players listen and respond, whether the album has a shape across its length, and whether the recording captures the density of the ensemble without turning it into noise.',
    },
    'jazz-fusion': {
      sound:
        'Jazz fusion combines jazz improvisation with electric instruments, rock and funk rhythms and studio production. Albums range from loose jam-based records to tightly composed ensemble pieces.',
      listenFor:
        'Recording matters more than in most jazz styles because the electric sound is part of the design. The scorecard still asks whether the compositions and the playing hold up once the novelty of the sound has faded.',
    },
  },

  styleExamples: [
    'bebop', 'hard bop', 'cool jazz', 'modal jazz', 'free jazz', 'soul jazz', 'post-bop', 'spiritual jazz',
    'jazz fusion', 'big band', 'vocal jazz', 'latin jazz',
  ],
  shortGenreSuffixes: [' Jazz'],

  prompt: {
    evidenceExamples: 'a solo, a theme statement, a change in the rhythm section, a sequencing decision',
    fillerTracks: 'alternate takes, short interludes and bonus tracks',
    analysisFocus: 'composition, recording and performance. Name musicians, producers and engineers only from the FACT SHEET.',
    primaryKeywordExample: '"Miles Davis Kind of Blue review" or "John Coltrane A Love Supreme review"',
    secondaryKeywordExample: '"Kind of Blue album analysis", "best modal jazz albums", "Miles Davis 1959 album"',
    headingExamples:
      '"Miles Davis Kind of Blue — How Jazz Learned to Leave Space", "John Coltrane A Love Supreme — Four Parts, One Prayer"',
  },

  voices: [
    {
      name: 'Calder',
      title: 'Lineage Voice',
      initials: 'C',
      bio: 'The house voice for jazz history: where an album sits in the lineage from the swing era onward, who it learned from and who learned from it.',
      specialization: 'Lineage',
      featured: true,
      avatarHue: 210,
      lens: `LENS: Lineage
Place the album in jazz history: what it answered, which players and records it learned from, which ones learned from it.
Voice: direct, confident, willing to disagree with the consensus.`,
      match: {
        artists: ['miles davis', 'john coltrane', 'duke ellington', 'louis armstrong', 'charles mingus', 'thelonious monk', 'dave brubeck', 'sonny rollins', 'bill evans', 'herbie hancock', 'wayne shorter', 'art blakey', 'charlie parker', 'dizzy gillespie', 'count basie', 'stan getz', 'cannonball adderley'],
        labels: ['columbia', 'blue note', 'prestige', 'verve', 'riverside'],
        keywords: ['classic', 'landmark', 'canon', 'lineage', 'influential', 'legacy'],
      },
    },
    {
      name: 'Nadia',
      title: 'Rhythm Section & Interplay Voice',
      initials: 'N',
      bio: 'Listens from the rhythm section: bass, drums and piano or guitar, and how a band talks to itself behind and between the solos.',
      specialization: 'Interplay',
      featured: true,
      avatarHue: 160,
      lens: `LENS: Rhythm section and interplay
Hear the album from the bandstand's engine room: how the bass and drums lay time, how the comping responds to the soloist, how the group's feel shifts over a track.
Voice: attentive and exact about what the players do together.`,
      match: {
        artists: ['bill evans', 'paul chambers', 'ron carter', 'elvin jones', 'tony williams', 'jimmy garrison', 'mccoy tyner', 'oscar peterson', 'ahmad jamal', 'keith jarrett', 'brad mehldau', 'art blakey', 'max roach', 'philly joe jones', 'charlie haden', 'scott la faro', 'red garland', 'wynton kelly'],
        labels: ['riverside', 'impulse!', 'verve', 'blue note'],
        keywords: ['trio', 'quartet', 'interplay', 'rhythm section', 'piano trio', 'rhythm'],
      },
    },
    {
      name: 'Hollis',
      title: 'Recording & Engineering Voice',
      initials: 'H',
      bio: 'Listens recording-first: room, microphone placement, stereo image and tape, and the engineers who shaped how jazz records sound.',
      specialization: 'Recording',
      featured: true,
      avatarHue: 30,
      lens: `LENS: Recording first
Listen to the album as a document: room sound, balance, stereo placement, tape character, the bass in the mix and what the engineering chooses to bring forward. Treat engineers and producers named in the FACT SHEET as co-authors.
Voice: precise and practical, technical without jargon for its own sake.`,
      match: {
        artists: ['sonny clark', 'lee morgan', 'horace silver', 'hank mobley', 'jimmy smith', 'freddie hubbard', 'joe henderson', 'kenny dorham', 'grant green', 'dexter gordon', 'lou donaldson', 'stanley turrentine', 'eric dolphy', 'andrew hill', 'bobby hutcherson'],
        labels: ['blue note', 'prestige', 'riverside', 'impulse!', 'contemporary', 'pacific jazz'],
        keywords: ['recording', 'engineer', 'van gelder', 'stereo', 'mono', 'remaster', 'sound'],
      },
    },
    {
      name: 'Mira',
      title: 'Composition & Arrangement Voice',
      initials: 'M',
      bio: 'Reads the written side of jazz: themes, harmonic ideas, arrangements and the frameworks that give improvisation its shape.',
      specialization: 'Composition',
      featured: true,
      avatarHue: 280,
      lens: `LENS: The writing
Study how the material is built: themes, harmony, form, arrangement, and how the written framework shapes what the soloists do with it. Explain the mechanics of what works and what doesn't.
Voice: incisive and analytical, never decorative.`,
      match: {
        artists: ['duke ellington', 'billy strayhorn', 'charles mingus', 'thelonious monk', 'wayne shorter', 'gil evans', 'george russell', 'maria schneider', 'carla bley', 'wynton marsalis', 'oliver nelson', 'dave brubeck', 'tadd dameron', 'gerry mulligan', 'jimmy giuffre', 'henry threadgill', 'horace silver', 'herbie hancock'],
        labels: ['columbia', 'atlantic', 'blue note', 'verve'],
        keywords: ['composition', 'arrangement', 'suite', 'writing', 'orchestration', 'harmony'],
      },
    },
    {
      name: 'Everett',
      title: 'Bebop & Hard Bop Voice',
      initials: 'E',
      bio: 'Covers bebop, hard bop and soul jazz: the small-group tradition of the 1940s through the 1960s and the players who defined it.',
      specialization: 'Bop',
      featured: false,
      avatarHue: 20,
      lens: `LENS: Bop and hard bop
Hear the album against the small-group tradition: bebop language, hard bop groove, blues and gospel roots. Know what is earned and what is borrowed from the greats.
Voice: unhurried, precise, comparative.`,
      match: {
        artists: ['charlie parker', 'dizzy gillespie', 'bud powell', 'thelonious monk', 'max roach', 'art blakey', 'horace silver', 'clifford brown', 'lee morgan', 'hank mobley', 'sonny stitt', 'wes montgomery', 'jimmy smith', 'cannonball adderley', 'lou donaldson', 'dexter gordon', 'sonny rollins', 'art pepper'],
        labels: ['blue note', 'prestige', 'savoy', 'riverside', 'dial'],
        keywords: ['bebop', 'bop', 'hard bop', 'soul jazz', 'jazz messengers', 'organ'],
      },
    },
    {
      name: 'Anselm',
      title: 'ECM & European Voice',
      initials: 'A',
      bio: 'Follows jazz outside the American mainstream: Europe, ECM-style chamber textures, and what other scenes added to the music.',
      specialization: 'European',
      featured: false,
      avatarHue: 190,
      lens: `LENS: Beyond the American mainstream
Position the album in the wider jazz world: ECM's spacious sound, Scandinavian and German scenes, British and French jazz, folk and classical colors. Notice what is local and what travels.
Voice: comparative, patient, attuned to silence and space.`,
      match: {
        artists: ['keith jarrett', 'jan garbarek', 'pat metheny', 'chick corea', 'ralph towner', 'paul bley', 'tomasz stanko', 'terje rypdal', 'enrico rava', 'charlie haden', 'jack dejohnette', 'eberhard weber', 'bobo stenson', 'esbjorn svensson trio', 'django reinhardt', 'stephane grappelli', 'nils petter molvaer', 'john surman', 'kenny wheeler'],
        labels: ['ecm', 'blue note', 'act', 'ogun', 'hat hut'],
        keywords: ['ecm', 'european', 'scandinavian', 'norwegian', 'german', 'nordic', 'chamber'],
      },
    },
    {
      name: 'Zane',
      title: 'Free & Avant-Garde Voice',
      initials: 'Z',
      bio: 'Covers free jazz and the avant-garde: collective improvisation, extended technique and records that treat form as an open question.',
      specialization: 'Avant-garde',
      featured: false,
      avatarHue: 350,
      lens: `LENS: Free and avant-garde
Hear the album through free jazz and experimental improvisation: how it handles density, silence, timbre and form, and whether the freedom builds something across the length of the record.
Voice: patient and exact; never confuse noise with depth.`,
      match: {
        artists: ['ornette coleman', 'cecil taylor', 'albert ayler', 'sun ra', 'archie shepp', 'pharoah sanders', 'anthony braxton', 'art ensemble of chicago', 'eric dolphy', 'sam rivers', 'peter brotzmann', 'derek bailey', 'don cherry', 'henry threadgill', 'roscoe mitchell', 'muhal richard abrams', 'john zorn', 'matthew shipp'],
        labels: ['impulse!', 'esp-disk', 'atlantic', 'hat hut', 'fmp', 'delmark'],
        keywords: ['free jazz', 'avant-garde', 'improvisation', 'aacm', 'experimental', 'loft'],
      },
    },
    {
      name: 'Jonah',
      title: 'Modern Era Voice',
      initials: 'J',
      bio: 'Covers jazz since 2000: post-bop revivals, genre-crossing records and the younger generation, and is the voice most likely to push back on the canon.',
      specialization: 'Modern',
      featured: false,
      avatarHue: 50,
      lens: `LENS: The modern era
Test the canon instead of deferring to it. Understand how hip-hop, electronic music, streaming and a global scene changed what a jazz record can be; judge new sounds on their own terms.
Voice: sharp, contrarian when the evidence supports it.`,
      match: {
        artists: ['kamasi washington', 'robert glasper', 'esperanza spalding', 'christian scott', 'ambrose akinmusire', 'vijay iyer', 'brad mehldau', 'the bad plus', 'snarky puppy', 'shabaka hutchings', 'makaya mccraven', 'nubya garcia', 'kendrick scott', 'jason moran', 'cecile mclorin salvant', 'tigran hamasyan', 'joshua redman', 'avishai cohen', 'gregory porter'],
        labels: ['blue note', 'brainfeeder', 'impulse!', 'concord', 'edition'],
        keywords: ['contemporary', 'nu jazz', 'modern', 'hip-hop', 'electronic', 'london'],
        yearAfter: 2000,
      },
    },
    {
      name: 'Lucille',
      title: 'Big Band & Vocal Voice',
      initials: 'L',
      bio: 'Follows large ensembles and singers: swing-era orchestras, vocal jazz and the songbook tradition, where arrangement and phrasing carry the music.',
      specialization: 'Big band & vocal',
      featured: false,
      avatarHue: 340,
      lens: `LENS: Ensembles and voices
Hear the album through big band and vocal jazz: arrangement, section writing, phrasing, lyric delivery and how a singer or an orchestra shapes a standard. Judge the interpretation, not just the song.
Voice: warm, exact, attentive to phrasing.`,
      match: {
        artists: ['duke ellington', 'count basie', 'ella fitzgerald', 'billie holiday', 'sarah vaughan', 'frank sinatra', 'nina simone', 'carmen mcrae', 'louis armstrong', 'benny goodman', 'woody herman', 'stan kenton', 'thad jones', 'mel lewis', 'maria schneider', 'betty carter', 'dinah washington', 'anita o\'day', 'tony bennett', 'johnny hartman'],
        labels: ['verve', 'columbia', 'decca', 'capitol', 'emarcy'],
        keywords: ['big band', 'orchestra', 'vocal', 'singer', 'swing', 'songbook', 'standards'],
      },
    },
    {
      name: 'Imani',
      title: 'Social Context Voice',
      initials: 'I',
      bio: 'Reads jazz in its moment: civil rights, spiritual searching and the social worlds that produced the records, and whether the music matches its statement.',
      specialization: 'Context',
      featured: false,
      avatarHue: 130,
      lens: `LENS: Context and meaning
Read the album as a statement: the civil rights era, spiritual seeking, politics and the world that shaped it, and whether the music carries the weight of what the title and framing claim. Paraphrase; never quote lyrics.
Voice: contextual and accessible, never academic for its own sake.`,
      match: {
        artists: ['john coltrane', 'alice coltrane', 'pharoah sanders', 'max roach', 'charles mingus', 'nina simone', 'archie shepp', 'sun ra', 'abbey lincoln', 'gil scott-heron', 'horace tapscott', 'yusef lateef', 'billie holiday', 'kamasi washington', 'terence blanchard', 'wayne shorter', 'lee morgan', 'marion brown'],
        labels: ['impulse!', 'strata-east', 'black jazz', 'candid', 'flying dutchman'],
        keywords: ['spiritual', 'civil rights', 'political', 'protest', 'africa', 'cosmic', 'suite'],
      },
    },
  ],

  gapTemplates: {
    bestOfYear: 'Best Jazz Albums of {year}',
    era: 'Jazz in the {era}',
    subgenresExplained: 'Jazz Styles Explained',
  },
};
